import { faExclamationCircle, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { usePopup } from 'custom_hooks/usePopup';
import { ReactNode } from 'react';
import { PopupPlacement, TriggeredPopup } from 'shared/triggered_popup/TriggeredPopup';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { stopEvent } from 'utils/event';

const ContentContainer = styled.div`
    max-width: 30rem;
    font-size: 1.4rem;
`;

const StyledTriggeredPopup = styled(TriggeredPopup)`
    border: 1px solid ${tokens.border.default};
`;

export type InfoPopupProps = Readonly<{
    title: string;
    triggerClassName?: string;
    triggerStyle?: React.CSSProperties;
    children: ReactNode;
    type?: 'info' | 'error';
    width?: string;
    placement?: PopupPlacement;
}>;

export function InfoPopup({
    title,
    triggerClassName,
    triggerStyle,
    children,
    type,
    width,
    placement,
}: InfoPopupProps) {
    const { triggerRef, popupRef, isOpen, toggleOpen, close } = usePopup({
        closeOnClickOutside: true,
    });

    const isErrorType = type === 'error';

    return (
        <>
            <HoverIconButton
                ref={triggerRef}
                title={title}
                ariaLabel={`${title} ${isErrorType ? 'Error' : 'Info'} Tooltip`}
                aria-expanded={isOpen}
                icon={isErrorType ? faExclamationCircle : faInfoCircle}
                hoverIcon={isErrorType ? faExclamationCircle : faInfoCircle}
                color={isErrorType ? tokens.error.default : tokens.icon.actionActive}
                hoverColor={isErrorType ? tokens.error.hover : tokens.icon.actionHover}
                onClick={(e) => {
                    stopEvent(e);
                    toggleOpen();
                }}
                className={triggerClassName}
                style={triggerStyle}
            />
            {isOpen && (
                <StyledTriggeredPopup
                    ref={popupRef}
                    anchorRef={triggerRef}
                    onClose={close}
                    placement={placement}
                    test-id="InfoPopup__Popup"
                >
                    <ContentContainer
                        style={{ maxWidth: width }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {children}
                    </ContentContainer>
                </StyledTriggeredPopup>
            )}
        </>
    );
}
